/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
} from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import MainLayout from '../../components/common/MainLayout';
import SimCornerControls from '../../components/common/SimCornerControls';
import PlanEditor from '../../components/grid/PlanEditor';
import {
  useGetPlotByIdQuery,
  useUpdatePlotMutation,
  useDeletePlotMutation,
} from '../../api/fieldsApi';
import { useGetPlotPlanQuery, usePatchPlotPlanMutation } from '../../api/planApi';
import type { Plot } from '../../models/Plot';
import type { PlanCellKind, PlanOperation, PlotPlan, PlanBedDeletionConflict, PlanCoordinateConflict } from '../../types/plan';
import { toastError, toastSuccess } from '../../utils/toast';
import { hasAction } from '../../types/permissions';
import { planCellKey } from '../../utils/planCells';
import { getConflictWorld } from '../../utils/planCoords';
import { isBedDeletionConflict, isCoordinateConflict } from '../../utils/planConflicts';
import { validateContiguity } from '../../utils/contiguity';
import { formatBedDisplayName } from '../../utils/bedDisplayName';
import { useSave } from '../../contexts/WorldContext';

const styles = {
  rowHeader: css({ marginBottom: 24 }),
  rowTitle: css({ marginBottom: 12, color: '#1890ff' }),
  bedItem: css({ cursor: 'pointer' }),
  bedDetails: css({ marginTop: 4, color: '#666' }),
  bedSpacing: css({ marginRight: 16 }),
  bedDescription: css({ marginTop: 4, color: '#999', fontSize: '0.9em' }),
  actionButtonsSpacing: css({ marginBottom: 16 }),
  bedsSection: css({ marginTop: 16 }),
  emptyStateCard: css({ marginTop: 16 }),
  plotSubtitle: css({ marginTop: 8, marginBottom: 16, color: '#666', fontSize: '14px' }),
};

const PlotPage: React.FC = () => {
  const params = useParams<{ saveId: string; fieldId?: string; plotId: string }>();
  const { plotId, saveId } = params;
  const navigate = useNavigate();
  const save = useSave();
  const user = useSelector((state: any) => state.auth.user);

  const {
    data: plotResponse,
    isLoading: plotLoading,
    isError: plotError,
    refetch,
  } = useGetPlotByIdQuery(
    { saveId: saveId ?? '', plotId: plotId ?? '' },
    { skip: !saveId || !plotId, refetchOnMountOrArgChange: true }
  );
  
  // farmId всегда из save context; fieldId — из URL или плана.
  const farmId = save.farmId ?? plotResponse?.field?.farmId?.toString();
  const fieldId = params.fieldId ?? plotResponse?.plot?.fieldId?.toString();
  const [updatePlot, { isLoading: updating }] = useUpdatePlotMutation();
  const [deletePlot] = useDeletePlotMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [, setBulkPlantingOpen] = useState(false);
  const [showTableView, setShowTableView] = useState(false);
  const [bedModalOpen, setBedModalOpen] = useState(false);
  const [bedCells, setBedCells] = useState<Array<{ row: number; col: number }>>([]);
  const [conflictKeys, setConflictKeys] = useState<Set<string>>(new Set());
  const [editForm] = Form.useForm();
  const [bedForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const canEditPlan = hasAction(user, 'plots', 'plots.update', farmId) || hasAction(user, 'beds', 'beds.create', farmId);

  const {
    data: planData,
    isLoading: planLoading,
  } = useGetPlotPlanQuery(
    { saveId: saveId ?? '', plotId: plotId || '', fieldId },
    { skip: !saveId || !plotId }
  );
  const [patchPlan, { isLoading: isPatching }] = usePatchPlotPlanMutation();

  const [localPlan, setLocalPlan] = useState<PlotPlan | null>(null);

  useEffect(() => {
    if (planData) {
      setLocalPlan(planData);
      setConflictKeys(new Set());
    }
  }, [planData]);

  const plot: Plot | undefined = plotResponse?.plot;
  const fieldFromResponse = plotResponse?.field;
  const field = fieldFromResponse;
  const plotName = plot?.name || plotId;
  const actualFieldId = fieldId || field?.id;
  const actions = useMemo(() => plotResponse?.actions ?? plot?.actions ?? [], [plotResponse, plot]);
  const plotArea = plot?.area ? ` (${plot.area} га)` : '';

  const handleOpenSettings = () => {
    if (!plot) return;
    editForm.setFieldsValue({
      name: plot.name,
      area: plot.area ?? undefined,
      location_coords: plot.location_coords ?? undefined,
      description: plot.description ?? undefined,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!plot || !plotId) return;
    try {
      const values = await editForm.validateFields();
      await updatePlot({ saveId: saveId || '', id: String(plot.id), payload: values }).unwrap();
      setEditOpen(false);
      refetch();
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteFromModal = () => {
    if (!plot || !plotId) return;
    setEditOpen(false);
    modal.confirm({
      title: 'Удалить участок?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deletePlot({ saveId: saveId || '', plotId: String(plot.id), fieldId: actualFieldId ? String(actualFieldId) : undefined }).unwrap();
          navigate(actualFieldId ? `/saves/${saveId}/fields/${actualFieldId}/plan` : `/saves/${saveId}`);
        } catch (error) {
          toastError(error);
        }
      },
    });
  };

  const handlePatchError = (error: any) => {
    const status = error?.status ?? error?.originalStatus;
    if (status === 403) {
      toastError('Ограничение тарифа: выбранный размер недоступен');
      return;
    }
    if (status === 409) {
      const conflicts = error?.data?.conflicts || [];

      const bedDeletionConflicts = conflicts.filter(isBedDeletionConflict);
      const coordinateConflicts = conflicts.filter(isCoordinateConflict);

      if (bedDeletionConflicts.length > 0) {
        handleBedDeletionConflicts(bedDeletionConflicts);
      } else if (coordinateConflicts.length > 0) {
        handleCoordinateConflicts(coordinateConflicts);
      }
      return;
    }
    toastError(error);
  };

  const handlePatch = async (operations: PlanOperation[]) => {
    if (!plotId) return;
    try {
      const updated = await patchPlan({ 
        saveId: saveId || '',
        plotId: plotId,
        fieldId,
        operations 
      }).unwrap();
      setLocalPlan(updated);
      setConflictKeys(new Set());
      refetch(); // Refresh plot data to get updated beds
    } catch (error: any) {
      handlePatchError(error);
    }
  };

  const handleBedDeletionConflicts = (conflicts: PlanBedDeletionConflict[]) => {
    const totalPlantings = conflicts.reduce((sum, c) => sum + c.activePlantings, 0);
    const bedIds = conflicts.map(c => c.bedId);
    
    Modal.error({
      title: 'Невозможно удалить грядку',
      content: (
        <div>
          <p>
            {conflicts.length === 1
              ? `Грядка ${bedIds[0]} содержит ${totalPlantings} ${totalPlantings === 1 ? 'активную посадку' : 'активных посадок'}.`
              : `Грядки ${bedIds.join(', ')} содержат ${totalPlantings} активных посадок.`}
          </p>
          <p style={{ marginTop: 12, color: '#666', fontSize: '14px' }}>
            Перед удалением грядки необходимо:
          </p>
          <ul style={{ marginTop: 8, paddingLeft: 20, color: '#666', fontSize: '14px' }}>
            <li>Завершить жизненный цикл посадок (продать, списать)</li>
            <li>Или пересадить растения на другую грядку</li>
          </ul>
        </div>
      ),
      okText: bedIds.length === 1 ? 'Открыть грядку' : 'Закрыть',
      onOk: () => {
        if (bedIds.length === 1) {
          navigate(`/saves/${saveId}/beds/${bedIds[0]}`);
        }
      },
    });
  };

  const handleCoordinateConflicts = (conflicts: PlanCoordinateConflict[]) => {
    if (!localPlan || !localPlan.boundingBox) return;
    const newKeys = new Set<string>();

    conflicts.forEach((conflict) => {
      const world = getConflictWorld(conflict);
      if (!world) return;
      // CN-0012: Use global coordinates directly
      newKeys.add(planCellKey(world.rowWorld, world.colWorld));
    });

    setConflictKeys(newKeys);
    
    Modal.error({
      title: 'Конфликт создания грядки',
      content: `Ячейки уже заняты. Помечены красным на сетке.`,
      okText: 'Закрыть',
    });
  };

  const handleBedSelection = (cells: Array<{ row: number; col: number }>) => {
    if (!canEditPlan) return;
    setBedCells(cells);
    setBedModalOpen(true);
  };

  const handleCreateBed = async () => {
    if (!bedCells.length) return;
    
    const contiguityError = validateContiguity(bedCells, 'Грядка');
    if (contiguityError) {
      toastError(contiguityError);
      return;
    }
    
    try {
      const values = await bedForm.validateFields();
      const bedPayload = Object.keys(values).reduce<Record<string, unknown>>((acc, key) => {
        if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
          acc[key] = values[key];
        }
        return acc;
      }, {});

      const updated = await patchPlan({
        saveId: saveId || '',
        plotId,
        fieldId,
        operations: [
          {
            op: 'createBed',
            cells: bedCells,
            bed: bedPayload,
          },
        ],
      }).unwrap();

      setLocalPlan(updated);
      setConflictKeys(new Set());
      refetch();
      toastSuccess('Грядка создана');
      bedForm.resetFields();
      setBedCells([]);
      setBedModalOpen(false);
    } catch (error) {
      handlePatchError(error);
    }
  };

  const paintKinds = useMemo<PlanCellKind[]>(() => ['path', 'obstacle'], []);

  const bedTableData = useMemo(() => {
    if (!plot?.beds || !Array.isArray(plot.beds)) return [];
    return [...plot.beds]
      .sort((a: any, b: any) => {
        const rowA = a.row_number ?? Number.MAX_SAFE_INTEGER;
        const rowB = b.row_number ?? Number.MAX_SAFE_INTEGER;
        if (rowA !== rowB) return rowA - rowB;
        const posA = a.position_number ?? Number.MAX_SAFE_INTEGER;
        const posB = b.position_number ?? Number.MAX_SAFE_INTEGER;
        return posA - posB;
      })
      .map((bed: any) => ({
        id: String(bed.id),
        name: formatBedDisplayName(bed.name, bed.anchor_code, String(bed.id)),
        plantingsCount: Number(bed.plantings_count ?? 0),
      }));
  }, [plot?.beds]);

  return (
    <MainLayout>
      {contextHolder}
      <h2>Участок: {plotName}{plotArea}</h2>
      {plot?.description && <div css={styles.plotSubtitle}>{plot.description}</div>}
      {plotLoading && <Spin />}
      {plotError && <Card>Не удалось загрузить данные участка</Card>}
      {!plot && !plotLoading && !plotError && <Card>Данные участка отсутствуют</Card>}
      {plot && !plotLoading && !plotError && (
        <>
          <Space css={styles.actionButtonsSpacing}>
            {actions.some((a: any) => a.code === 'plots.update' || a.code === 'plots.delete') && (
              <Button
                icon={<SettingOutlined />}
                onClick={handleOpenSettings}
              >
                Настройки
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setBulkPlantingOpen(true)}
            >
              Массовое создание
            </Button>
            <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Табличный вид</span>
              <Switch checked={showTableView} onChange={setShowTableView} />
            </div>
          </Space>

          {!showTableView && (
            <>
              {planLoading || !localPlan || !farmId ? (
                <Card>
                  <Spin />
                </Card>
              ) : (
                <PlanEditor
                  boundingBox={localPlan.boundingBox}
                  allowedBoundingBox={localPlan.allowedBoundingBox}
                  globalGrid={localPlan.globalGrid}
                  cells={localPlan.cells}
                  structuralObjects={localPlan.structuralObjects}
                  beds={localPlan.beds}
                  paintKinds={paintKinds}
                  saveId={saveId || ''}
                  farmId={farmId}  // CN-0012: UUID string, проверка !farmId выше
                  fieldId={fieldId || ''}
                  plotId={plotId || ''}
                  allowBedSelection={canEditPlan}
                  isBusy={isPatching}
                  highlightCells={conflictKeys}
                  onPatch={handlePatch}
                  onBedSelection={handleBedSelection}
                />
              )}
            </>
          )}

          {showTableView && bedTableData.length > 0 && (
            <div css={styles.bedsSection}>
              <h3>Грядки</h3>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={bedTableData}
                onRow={(record) => ({
                  onClick: () => navigate(`/saves/${saveId}/beds/${record.id}`),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  {
                    title: 'Грядка',
                    dataIndex: 'name',
                    key: 'name',
                    width: '60%',
                    render: (name: string) => <strong>{name}</strong>,
                  },
                  {
                    title: 'Посадки',
                    dataIndex: 'plantingsCount',
                    key: 'plantingsCount',
                    render: (count: number) => (
                      count > 0 ? <Tag color="green">{count}</Tag> : <span style={{ color: '#8c8c8c' }}>пусто</span>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {showTableView && plot.beds && Array.isArray(plot.beds) && plot.beds.length === 0 && (
            <Card css={styles.emptyStateCard} variant="borderless">
              Грядок пока нет.
            </Card>
          )}
        </>
      )}
      <Modal
        title="Настройки участка"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditOpen(false)}>
            Отмена
          </Button>,
          actions.some((a: any) => a.code === 'plots.delete') && (
            <Button key="delete" danger onClick={handleDeleteFromModal}>
              Удалить участок
            </Button>
          ),
          <Button key="save" type="primary" loading={updating} onClick={handleSaveEdit}>
            Сохранить
          </Button>,
        ]}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="Название" name="name" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Площадь (га)" name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label="Координаты" name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Создать грядку"
        open={bedModalOpen}
        onOk={handleCreateBed}
        onCancel={() => {
          setBedModalOpen(false);
          setBedCells([]);
        }}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={isPatching}
      >
        <Form form={bedForm} layout="vertical">
          <Form.Item label="Площадь (м²)" name="area">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Размер (см, код)" name="size_cm_encoded">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Геолокация" name="geo_location">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <SimCornerControls
        farmId={farmId}
        onAdvanceDone={() => {
          refetch();
        }}
      />
    </MainLayout>
  );
};

export default PlotPage;
